// src/HomePage.js - 安全版本，使用确实存在的图标
import React, { useState } from 'react';
import { 
  Layout, 
  Button, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Space, 
  Statistic,
  Avatar,
  Rate,
  Menu,
  Drawer
} from 'antd';
import {
  PieChartOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  CodeOutlined,
  RobotOutlined,
  LineChartOutlined,
  EyeOutlined,
  MenuOutlined,
  RightOutlined,
  PlayCircleOutlined,
  StarOutlined,
  SettingOutlined,
  TeamOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import './HomePage.css';

const { Header, Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;

function HomePage() {
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuVisible(false);
  };

  const features = [
    {
      icon: <DatabaseOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: "数据导入",
      description: "支持多种数据格式，快速导入您的数据源，无缝对接各类数据库"
    },
    {
      icon: <CodeOutlined style={{ fontSize: '32px', color: '#52c41a' }} />,
      title: "在线R编辑器",
      description: "强大的在线R代码编辑环境，实时预览，支持复杂的数据分析与处理"
    },
    {
      icon: <RobotOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      title: "DeepSeek AI助手",
      description: "智能AI助手协助您编写代码，优化图表，提供专业的数据分析建议"
    },
    {
      icon: <BarChartOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      title: "丰富图表类型",
      description: "涵盖统计图表、交互式可视化、地理信息图表等多种专业图表类型"
    },
    {
      icon: <LineChartOutlined style={{ fontSize: '32px', color: '#f5222d' }} />,
      title: "深度洞察",
      description: "智能数据分析，自动发现数据模式，生成专业的洞察报告"
    },
    {
      icon: <EyeOutlined style={{ fontSize: '32px', color: '#13c2c2' }} />,
      title: "实时预览",
      description: "所见即所得的编辑体验，实时预览图表效果，快速迭代优化"
    }
  ];

  // 报告中心是独立部署的页面，走整页跳转
  const goToReports = () => {
    setMobileMenuVisible(false);
    window.location.href = '/reports/';
  };

  const goToLogin = () => {
    setMobileMenuVisible(false);
    window.location.href = process.env.REACT_APP_LOGIN_URL;
  };

  const menuItems = [
    { 
      key: 'features', 
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <StarOutlined style={{ fontSize: '14px' }} />
          功能特色
        </span>
      ), 
      onClick: () => scrollToSection('features') 
    },
    { 
      key: 'process', 
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <SettingOutlined style={{ fontSize: '14px' }} />
          使用流程
        </span>
      ), 
      onClick: () => scrollToSection('process') 
    },
    { 
      key: 'testimonials', 
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <TeamOutlined style={{ fontSize: '14px' }} />
          客户案例
        </span>
      ), 
      onClick: () => scrollToSection('testimonials') 
    }
  ];

  const testimonials = [
    {
      name: "张明",
      company: "中科院某所",
      role: "博士后",
      content: "这个平台丰富了我青基的研究基础部分，大大提升了我们的数据分析效率，AI助手特别智能，能快速生成高质量的可视化图表。",
      rating: 5,
      avatar: "ZM"
    },
    {
      name: "李华",
      company: "天津985高校",
      role: "教授",
      content: "分析平台高效，带动了组内代码分析的发展。把SPSS替换成R语言分析是我一直想做的事情，平台帮助我的课题组推进了这个想法。",
      rating: 5,
      avatar: "LH"
    },
    {
      name: "王强",
      company: "上海985高校",
      role: "博士",
      content: "对接人员都是业内师兄师姐，希望以后能够加入他们。",
      rating: 5,
      avatar: "WQ"
    }
  ];

  return (
    <Layout className="min-h-screen">
      {/* Header */}
      <Header className="header-fixed">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-icon">
              <PieChartOutlined />
            </div>
            <span className="logo-text">常瑞智谱 ZhiPlot</span>
          </div>
          
          <div className="desktop-menu">
            <Menu mode="horizontal" style={{ border: 'none', background: 'transparent' }}>
              {menuItems.map(item => (
                <Menu.Item key={item.key} onClick={item.onClick}>
                  {item.label}
                </Menu.Item>
              ))}
            </Menu>
          </div>
          
          <div className="desktop-buttons">
            <Button icon={<FileTextOutlined />} onClick={goToReports}>报告中心</Button>
            <Button type="primary" className="gradient-button" onClick={goToLogin}>登录</Button>
          </div>

          {/* 手机端顶栏的登录入口：desktop-buttons 在窄屏被隐藏，
              若不补这个按钮，用户必须先发现汉堡菜单才能登录 */}
          <Button
            className="mobile-login-btn gradient-button"
            type="primary"
            size="small"
            onClick={goToLogin}
          >
            登录
          </Button>

          <Button
            className="mobile-menu-btn"
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
          />
        </div>
      </Header>

      {/* Mobile Menu Drawer */}
      <Drawer
        title="菜单"
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={300}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {menuItems.map(item => (
            <div key={item.key} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }} onClick={item.onClick}>
              {item.label}
            </div>
          ))}
          <div style={{ paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Button icon={<FileTextOutlined />} block onClick={goToReports}>报告中心</Button>
            <Button type="primary" block onClick={goToLogin}>登录</Button>
          </div>
        </div>
      </Drawer>

      <Content style={{ marginTop: '64px' }}>
        {/* Hero Section */}
        <div className="hero-section">
          <div className="container">
            <div className="hero-badge">
              ⚡ AI驱动的数据可视化平台
            </div>
            
            <Title level={1} className="hero-title">
              让数据<span className="gradient-text">讲述故事</span>
            </Title>
            
            <Paragraph className="hero-description">
              专业的数据可视化平台，帮助您快速创建令人印象深刻的图表，深入洞察数据价值。
              结合强大的R语言支持和DeepSeek AI智能助手，让数据分析变得简单高效。
            </Paragraph>

            {/* Stats */}
            <div style={{ marginTop: '64px', width: '100%' }}>
              <Row gutter={[24, 24]} justify="center" style={{ maxWidth: '800px', margin: '0 auto' }}>
                <Col xs={12} md={6}>
                  <div style={{ 
                    textAlign: 'center', 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      color: '#666', 
                      fontSize: '14px', 
                      marginBottom: '8px',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      活跃用户
                    </div>
                    <div style={{ 
                      color: '#1890ff', 
                      fontSize: '2rem', 
                      fontWeight: 'bold',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      500+
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ 
                    textAlign: 'center', 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      color: '#666', 
                      fontSize: '14px', 
                      marginBottom: '8px',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      图表生成
                    </div>
                    <div style={{ 
                      color: '#52c41a', 
                      fontSize: '2rem', 
                      fontWeight: 'bold',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      10,000+
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ 
                    textAlign: 'center', 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      color: '#666', 
                      fontSize: '14px', 
                      marginBottom: '8px',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      企业客户
                    </div>
                    <div style={{ 
                      color: '#722ed1', 
                      fontSize: '2rem', 
                      fontWeight: 'bold',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      20+
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ 
                    textAlign: 'center', 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      color: '#666', 
                      fontSize: '14px', 
                      marginBottom: '8px',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      满意度
                    </div>
                    <div style={{ 
                      color: '#fa541c', 
                      fontSize: '2rem', 
                      fontWeight: 'bold',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      99%
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="features-section">
          <div className="container">
            <div className="section-header">
              <Title level={2}>强大功能，简单易用</Title>
              <Paragraph>
                集成最新的AI技术和专业的数据分析工具，为您提供一站式数据可视化解决方案
              </Paragraph>
            </div>
            
            <Row gutter={[32, 32]}>
              {features.map((feature, index) => (
                <Col xs={24} md={12} lg={8} key={index}>
                  <Card className="feature-card">
                    <div className="feature-icon">
                      {feature.icon}
                    </div>
                    <Title level={4}>{feature.title}</Title>
                    <Paragraph>{feature.description}</Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {/* Process Section */}
        <div id="process" className="process-section">
          <div className="container">
            <div className="section-header">
              <Title level={2}>四步创建专业图表</Title>
              <Paragraph>简单四步，从数据到洞察，让复杂的数据分析变得轻松愉快</Paragraph>
            </div>
            
            <Row gutter={[32, 32]}>
              {[
                { step: 1, title: "上传数据", desc: "导入您的数据文件或连接数据源，支持CSV、Excel、数据库等多种格式", color: '#1890ff' },
                { step: 2, title: "智能分析", desc: "AI助手自动分析数据特征，推荐最佳可视化方案和图表类型", color: '#722ed1' },
                { step: 3, title: "编写代码", desc: "使用在线R编辑器编写代码，或让AI自动生成最优代码", color: '#52c41a' },
                { step: 4, title: "生成图表", desc: "一键生成专业图表，支持多种导出格式，轻松分享与协作", color: '#fa541c' }
              ].map((item, index) => (
                <Col xs={24} md={6} key={index}>
                  <Card className="process-card">
                    <div className="process-number" style={{ background: `linear-gradient(135deg, ${item.color}, ${item.color}99)` }}>
                      {item.step}
                    </div>
                    <Title level={4}>{item.title}</Title>
                    <Paragraph>{item.desc}</Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {/* Testimonials Section */}
        <div id="testimonials" className="testimonials-section">
          <div className="container">
            <div className="section-header">
              <Title level={2}>客户好评如潮</Title>
              <Paragraph>来自各行各业专业人士的真实反馈</Paragraph>
            </div>
            
            <Row gutter={[32, 32]}>
              {testimonials.map((testimonial, index) => (
                <Col xs={24} lg={8} key={index}>
                  <Card className="testimonial-card">
                    <Rate disabled defaultValue={testimonial.rating} style={{ marginBottom: '16px' }} />
                    <Paragraph className="testimonial-content">
                      "{testimonial.content}"
                    </Paragraph>
                    <div className="testimonial-author">
                      <Avatar size={48} className="testimonial-avatar">
                        {testimonial.avatar}
                      </Avatar>
                      <div>
                        <div className="author-name">{testimonial.name}</div>
                        <div className="author-info">
                          {testimonial.role} · {testimonial.company}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <div className="container">
            <Title level={2} className="cta-title">
              准备好开始您的数据可视化之旅了吗？
            </Title>
            <Paragraph className="cta-description">
              加入数万名专业人士的行列，让AI助您释放数据的真正价值
            </Paragraph>
          </div>
        </div>
      </Content>

      {/* Footer */}
      <Footer className="footer">
        <div className="container">
          <Row gutter={[32, 32]}>
            <Col xs={24} md={8}>
              <div className="footer-logo">
                <div className="logo-icon">
                  <PieChartOutlined />
                </div>
                <span className="logo-text">常瑞智谱 ZhiPlot</span>
              </div>
              <Paragraph style={{ color: 'rgba(255,255,255,0.8)' }}>
                专业的数据可视化平台，让每个人都能轻松创建令人印象深刻的数据图表。
              </Paragraph>
            </Col>
            <Col xs={12} md={4}>
              <Title level={5} className="footer-title">产品</Title>
              <div className="footer-links">
                <a href="#" className="footer-link">功能介绍</a>
                <a href="#" className="footer-link">价格方案</a>
                <a href="#" className="footer-link">API文档</a>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <Title level={5} className="footer-title">支持</Title>
              <div className="footer-links">
                <a href="#" className="footer-link">帮助中心</a>
                <a href="#" className="footer-link">联系我们</a>
                <a href="#" className="footer-link">社区论坛</a>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <Title level={5} className="footer-title">联系信息</Title>
              <div className="footer-contact">
                <div>📧 ruiaoma@163.com</div>
                <div>📞 17343377904</div>
                <div>📍 上海市闵行区剑川路</div>
              </div>
            </Col>
          </Row>
          <div className="footer-bottom">
            <Text style={{ color: 'rgba(255,255,255,0.8)' }}>© 2025 常瑞智谱 ZhiPlot. 保留所有权利。</Text>
          </div>
        </div>
      </Footer>
    </Layout>
  );
}

export default HomePage;